import { getPageTitle } from './pageTitles';

export const SITE_ORIGIN = 'https://propertiesinfinite.com';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.phoneix.pif';
export const APP_STORE_URL = 'https://apps.apple.com/in/app/properties-in-finite-pif/id6778318175';
export const PLAY_STORE_APP_ID = 'com.phoneix.pif';

export function buildCanonicalUrl(pathname = '/') {
  const path = String(pathname || '/').split('?')[0].split('#')[0] || '/';
  const normalized = path.replace(/\/+$/, '') || '/';
  if (normalized === '/') return `${SITE_ORIGIN}/`;
  return `${SITE_ORIGIN}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertMeta(name, content, attr = 'name') {
  if (content == null) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function setPageMeta({
  pathname = '/',
  title,
  description,
  canonical,
  robots,
}) {
  document.title = title || getPageTitle(pathname);
  upsertLink('canonical', canonical || buildCanonicalUrl(pathname));
  if (description) upsertMeta('description', description);
  if (robots) upsertMeta('robots', robots);
}

export function isAndroidMobile() {
  return /Android/i.test(navigator.userAgent || '');
}

export function isIosMobile() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS desktop UA
  return navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints || 0) > 1;
}

const INSTALLED_KEY = 'pif_native_app_installed';
/** Remembered install marks expire so uninstalls are not stuck forever. */
const INSTALLED_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Set when getInstalledRelatedApps has returned a positive hit at least once. */
const GIRA_OK_KEY = 'pif_gira_detection_ok';
/** Timestamp after user taps Get App / store — used to soft-detect a fresh install. */
const AWAITING_INSTALL_KEY = 'pif_awaiting_install_at';

/** True on phones / narrow touch devices (not “app installed”). */
export function isMobileWeb() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (isAndroidMobile() || isIosMobile()) return true;
  try {
    const narrow = window.matchMedia('(max-width: 900px)').matches;
    const touch = Number(navigator.maxTouchPoints || 0) > 0
      || window.matchMedia('(pointer: coarse)').matches;
    return narrow && touch;
  } catch {
    return false;
  }
}

function readInstalledMark() {
  try {
    const raw = localStorage.getItem(INSTALLED_KEY);
    if (!raw) return null;
    // Legacy plain "1" — migrate to timestamped mark (don't trust forever).
    if (raw === '1') {
      const migrated = { at: Date.now() };
      localStorage.setItem(INSTALLED_KEY, JSON.stringify(migrated));
      return migrated;
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.at === 'number') return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

/** Sync: remembered successful open only (may be stale until async re-check). */
export function isPifAppInstalledSync() {
  const mark = readInstalledMark();
  if (!mark) return false;
  return Date.now() - mark.at < INSTALLED_TTL_MS;
}

/**
 * @deprecated Use isPifAppInstalledSync / isPifAppInstalled.
 * Kept for callers — now means “known installed”, NOT “is mobile”.
 */
export function preferContinueInApp() {
  return isPifAppInstalledSync();
}

/** Custom scheme used by the native PIF app (Android + iOS). */
export const PIF_APP_SCHEME_HOME = 'pif://home';

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

const INSTALL_EVENT = 'pif-app-install';
const STORE_AFTER_KEY = 'pif_store_after_fallback';
const OPEN_KNOWN_KEY = 'pif_open_was_known';
const NOT_INSTALLED_HASH = '#pif-not-installed';

function emitInstall(installed) {
  try {
    window.dispatchEvent(new CustomEvent(INSTALL_EVENT, { detail: { installed: !!installed } }));
  } catch {
    /* ignore */
  }
}

function isRelatedPifApp(app) {
  if (!app || typeof app !== 'object') return false;
  const id = String(app.id || '').toLowerCase().trim();
  const url = String(app.url || '').toLowerCase();
  const platform = String(app.platform || '').toLowerCase();
  const playPlatform = platform === 'play' || platform === 'android' || platform === 'chromeos_play';
  if (playPlatform && (id === PLAY_STORE_APP_ID.toLowerCase() || id.includes('phoneix.pif'))) {
    return true;
  }
  if ((platform === 'itunes' || platform === 'ios') && (id.includes('6778318175') || url.includes('6778318175'))) {
    return true;
  }
  return (
    id === PLAY_STORE_APP_ID.toLowerCase()
    || id.includes('com.phoneix.pif')
    || id.includes('6778318175')
    || url.includes('com.phoneix.pif')
    || url.includes('id6778318175')
  );
}

/**
 * @returns {{ ok: boolean, found: boolean, apps: object[] }}
 * ok=true means the API ran; found=true means PIF is among related installed apps.
 */
async function queryInstalledRelatedAppsDetailed() {
  if (typeof navigator === 'undefined') return { ok: false, found: false, apps: [] };
  if (typeof navigator.getInstalledRelatedApps !== 'function') {
    return { ok: false, found: false, apps: [] };
  }
  try {
    const apps = await navigator.getInstalledRelatedApps();
    const list = Array.isArray(apps) ? apps : [];
    return { ok: true, found: list.some(isRelatedPifApp), apps: list };
  } catch {
    return { ok: false, found: false, apps: [] };
  }
}

function canUseInstalledRelatedApps() {
  return typeof navigator !== 'undefined'
    && typeof navigator.getInstalledRelatedApps === 'function';
}

function giraEverWorked() {
  try {
    return localStorage.getItem(GIRA_OK_KEY) === '1';
  } catch {
    return false;
  }
}

function markGiraOk() {
  try {
    localStorage.setItem(GIRA_OK_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Call when the user opens the Play Store / App Store from Get App.
 * Returning to the site shortly after is treated as a likely install.
 */
export function noteAppStoreVisit() {
  try {
    localStorage.setItem(AWAITING_INSTALL_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/** Sync helper for optimistic UI before async GIRA finishes. */
export function softInstalledAfterStoreVisitSync() {
  try {
    const raw = localStorage.getItem(AWAITING_INSTALL_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    const age = Date.now() - at;
    // User opened the store and came back (or reloads within 48h).
    if (age >= 500 && age < 48 * 60 * 60 * 1000) return true;
    if (age >= 48 * 60 * 60 * 1000) localStorage.removeItem(AWAITING_INSTALL_KEY);
  } catch {
    /* ignore */
  }
  return false;
}

function clearAwaitingInstall() {
  try {
    localStorage.removeItem(AWAITING_INSTALL_KEY);
  } catch {
    /* ignore */
  }
}

async function ensureManifestWarm() {
  if (typeof document === 'undefined') return;
  try {
    const link = document.querySelector('link[rel="manifest"]');
    const href = link?.href || `${window.location.origin}/manifest.json`;
    await fetch(href, { credentials: 'same-origin', cache: 'no-cache' });
  } catch {
    /* ignore */
  }
}

function stripNotInstalledHash() {
  if (typeof window === 'undefined') return false;
  if (window.location.hash !== NOT_INSTALLED_HASH) return false;
  const next = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(null, '', next || '/');
  return true;
}

function notInstalledFallbackUrl() {
  const url = new URL(window.location.href);
  url.hash = NOT_INSTALLED_HASH.slice(1);
  return url.toString();
}

function openStoreOnce() {
  try {
    if (sessionStorage.getItem('pif_store_opened') === '1') return;
    sessionStorage.setItem('pif_store_opened', '1');
  } catch {
    /* ignore */
  }
  noteAppStoreVisit();
  const storeUrl = isIosMobile() ? APP_STORE_URL : PLAY_STORE_URL;
  window.open(storeUrl, '_blank', 'noopener,noreferrer');
}

/**
 * A same-tab intent fallback (#pif-not-installed) means the app did not open.
 * A Continue attempt then clears the install mark. A Get App attempt opens the store.
 */
function consumeFailedOpen() {
  if (!stripNotInstalledHash()) return false;
  let knownAttempt = false;
  let openStore = false;
  try {
    knownAttempt = sessionStorage.getItem(OPEN_KNOWN_KEY) === '1';
    openStore = sessionStorage.getItem(STORE_AFTER_KEY) === '1';
    sessionStorage.removeItem(OPEN_KNOWN_KEY);
    sessionStorage.removeItem(STORE_AFTER_KEY);
  } catch {
    /* ignore */
  }
  if (knownAttempt) clearPifAppInstalled();
  if (openStore) openStoreOnce();
  return true;
}

/**
 * Detect whether the native PIF app is installed.
 *
 * - GIRA positive → installed
 * - GIRA negative after a previous positive hit → uninstalled
 * - Otherwise a remembered successful open (the page actually left for the app)
 */
export async function isPifAppInstalled() {
  if (typeof navigator === 'undefined') return false;
  if (consumeFailedOpen() && !isPifAppInstalledSync()) return false;

  if (canUseInstalledRelatedApps()) {
    if (typeof document !== 'undefined' && document.readyState !== 'complete') {
      await new Promise((resolve) => {
        window.addEventListener('load', resolve, { once: true });
        window.setTimeout(resolve, 1500);
      });
    }

    await ensureManifestWarm();

    for (let attempt = 0; attempt < 6; attempt += 1) {
      const result = await queryInstalledRelatedAppsDetailed();
      if (result.found) {
        markGiraOk();
        markPifAppInstalled();
        clearAwaitingInstall();
        return true;
      }
      if (attempt < 5) await sleep(400 * (attempt + 1));
    }

    const last = await queryInstalledRelatedAppsDetailed();
    if (last.ok && !last.found && giraEverWorked()) {
      clearPifAppInstalled();
      clearAwaitingInstall();
      return false;
    }
  }

  return isPifAppInstalledSync();
}

/**
 * Keep polling GIRA for a while (DAL verification can lag after assetlinks is fixed).
 * Uses a single GIRA probe per tick — not the full multi-retry path.
 */
export async function watchForPifAppInstall({
  timeoutMs = 20000,
  intervalMs = 2000,
  signal,
} = {}) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (signal?.aborted) return false;

    if (isPifAppInstalledSync()) return true;

    if (canUseInstalledRelatedApps()) {
      const result = await queryInstalledRelatedAppsDetailed();
      if (result.found) {
        markGiraOk();
        markPifAppInstalled();
        clearAwaitingInstall();
        return true;
      }
    }

    await sleep(intervalMs);
  }

  if (signal?.aborted) return false;
  return isPifAppInstalled();
}

/** Mark that the native app is present (after GIRA hit or a successful open). */
export function markPifAppInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, JSON.stringify({ at: Date.now() }));
    clearAwaitingInstall();
  } catch {
    /* ignore */
  }
  emitInstall(true);
}

/** Clear install mark after a confirmed uninstall. */
export function clearPifAppInstalled() {
  try {
    localStorage.removeItem(INSTALLED_KEY);
  } catch {
    /* ignore */
  }
  emitInstall(false);
}

/**
 * Parse /property|/project|/api/share/... path → listing deep-link params.
 */
export function parseListingDeepLinkFromPath(pathname = '') {
  const p = String(pathname || '');
  let m = p.match(/^\/(?:view\/)?(property|project)\/(\d+)(?:\/([^/?#]+))?/i);
  if (m) {
    return {
      propertyId: m[2],
      slug: m[3] || undefined,
      isProject: String(m[1]).toLowerCase() === 'project',
    };
  }
  m = p.match(/^\/api\/share\/(?:listing|property)\/(\d+)(?:\/([^/?#]+))?/i);
  if (m) {
    return {
      propertyId: m[1],
      slug: m[2] || undefined,
      isProject: false,
    };
  }
  return null;
}

function buildDeepLinkPath({ propertyId, slug, isProject = false } = {}) {
  const id = Number(propertyId);
  if (!Number.isFinite(id) || id <= 0) return 'home';
  const kind = isProject ? 'project' : 'property';
  const cleanSlug = slug ? String(slug).replace(/^\/+|\/+$/g, '') : '';
  return cleanSlug ? `${kind}/${id}/${cleanSlug}` : `${kind}/${id}`;
}

/** Custom-scheme URL the native app registers (pif://property/123). */
export function getPifSchemeUrl({ propertyId, slug, isProject = false } = {}) {
  const id = Number(propertyId);
  if (Number.isFinite(id) && id > 0) {
    // Always host "property" — matches Android intent-filter + iOS linking.
    const cleanSlug = slug ? String(slug).replace(/^\/+|\/+$/g, '') : '';
    return cleanSlug ? `pif://property/${id}/${cleanSlug}` : `pif://property/${id}`;
  }
  return PIF_APP_SCHEME_HOME;
}

/**
 * URL that opens the installed native app (home or a specific listing).
 */
export function getContinueAppUrl({
  propertyId,
  slug,
  isProject = false,
} = {}) {
  const id = Number(propertyId);
  const hasListing = Number.isFinite(id) && id > 0;
  const cleanSlug = slug ? String(slug).replace(/^\/+|\/+$/g, '') : '';
  const deepPath = buildDeepLinkPath({ propertyId, slug, isProject });
  const webUrl = hasListing
    ? `${SITE_ORIGIN}/${deepPath}`
    : `${SITE_ORIGIN}/`;
  const listingPath = hasListing
    ? (cleanSlug ? `property/${id}/${cleanSlug}` : `property/${id}`)
    : 'home';

  if (isAndroidMobile()) {
    // Stay on this page when the app is missing. A Play Store fallback hides the
    // tab and looks like a successful open.
    const fallback = `;S.browser_fallback_url=${encodeURIComponent(notInstalledFallbackUrl())}`;
    return `intent://${listingPath}#Intent;scheme=pif;package=${PLAY_STORE_APP_ID}${fallback};end`;
  }

  if (isIosMobile()) {
    return getPifSchemeUrl({ propertyId, slug, isProject });
  }

  return webUrl;
}

/**
 * Open the native app on the same listing (`pif://property/:id`).
 * If the app is installed the tab hides and later visits show Continue App.
 * If it is not, Get App continues to the store without treating that as an install.
 */
export function openContinueApp({
  knownInstalled = false,
  propertyId,
  slug,
  isProject = false,
  storeFallback = true,
} = {}) {
  const allowStore = storeFallback && !knownInstalled;
  const schemeUrl = getPifSchemeUrl({ propertyId, slug, isProject });
  const appUrl = isAndroidMobile() || isIosMobile()
    ? getContinueAppUrl({ propertyId, slug, isProject })
    : schemeUrl;

  try {
    sessionStorage.removeItem('pif_store_opened');
    sessionStorage.setItem(OPEN_KNOWN_KEY, knownInstalled ? '1' : '0');
    if (allowStore) sessionStorage.setItem(STORE_AFTER_KEY, '1');
    else sessionStorage.removeItem(STORE_AFTER_KEY);
  } catch {
    /* ignore */
  }

  let leftPage = false;
  let failedHere = false;

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') leftPage = true;
  };
  const onPageHide = () => {
    leftPage = true;
  };
  const onHash = () => {
    if (window.location.hash === NOT_INSTALLED_HASH) failedHere = true;
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('hashchange', onHash);

  const cleanup = () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('hashchange', onHash);
  };

  try {
    window.location.href = isIosMobile() ? schemeUrl : appUrl;
  } catch {
    window.location.href = schemeUrl;
  }

  window.setTimeout(() => {
    cleanup();
    const stillHere = document.visibilityState === 'visible';
    const hashFailed = failedHere || window.location.hash === NOT_INSTALLED_HASH;
    if (hashFailed) stripNotInstalledHash();

    if (leftPage && !hashFailed) {
      try {
        sessionStorage.removeItem(STORE_AFTER_KEY);
        sessionStorage.removeItem(OPEN_KNOWN_KEY);
      } catch {
        /* ignore */
      }
      markPifAppInstalled();
      return;
    }

    if (!stillHere) return;

    // The tab never left, or Chrome bounced back to this page. The app did not open.
    if (knownInstalled) clearPifAppInstalled();
    try {
      sessionStorage.removeItem(STORE_AFTER_KEY);
      sessionStorage.removeItem(OPEN_KNOWN_KEY);
    } catch {
      /* ignore */
    }
    if (allowStore) openStoreOnce();
  }, 1800);
}

let continueAfterStore = false;

/**
 * After Get App sends the user to the store, one return to this tab tries the app.
 * A successful open switches the label to Continue App. A miss leaves Get App.
 */
export function maybeContinueAfterInstallReturn(opts = {}) {
  if (continueAfterStore) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  let at = NaN;
  try {
    at = Number(localStorage.getItem(AWAITING_INSTALL_KEY));
  } catch {
    return;
  }
  if (!Number.isFinite(at)) return;
  const age = Date.now() - at;
  if (age < 800 || age > 15 * 60 * 1000) {
    if (age > 15 * 60 * 1000) clearAwaitingInstall();
    return;
  }
  continueAfterStore = true;
  clearAwaitingInstall();
  openContinueApp({
    knownInstalled: false,
    storeFallback: false,
    propertyId: opts.propertyId,
    slug: opts.slug,
    isProject: opts.isProject,
  });
  window.setTimeout(() => {
    continueAfterStore = false;
  }, 4000);
}

/** Keep Continue / Get App in sync after an open, a return from the store, or GIRA. */
export function subscribePifInstall(onChange, getLaunchOpts) {
  const onEvent = (event) => onChange(!!event.detail?.installed);
  window.addEventListener(INSTALL_EVENT, onEvent);
  let left = false;
  const onVis = () => {
    if (document.visibilityState === 'hidden') {
      left = true;
      return;
    }
    if (!left) return;
    left = false;
    const opts = typeof getLaunchOpts === 'function' ? getLaunchOpts() : {};
    maybeContinueAfterInstallReturn(opts);
    isPifAppInstalled().then((known) => onChange(!!known)).catch(() => {});
  };
  document.addEventListener('visibilitychange', onVis);
  return () => {
    window.removeEventListener(INSTALL_EVENT, onEvent);
    document.removeEventListener('visibilitychange', onVis);
  };
}
